<template>
  <div class="analysis-panel-wrapper">
    <!-- 1. 中间内容区 -->
    <div class="center-content-area">
      <div class="main-preview-area">
        <div class="carousel-wrapper">
          <el-carousel
            height="100%"
            indicator-position="none"
            :autoplay="false"
            @change="handleCarouselChange"
            ref="mainCarousel"
            arrow="always"
          >
            <el-carousel-item v-for="image in images" :key="image.image_id">
              <div class="carousel-image-container">
                <img
                  :src="getImageUrl(image.image_id, image.image_url)"
                  class="carousel-image"
                />
                <div
                  v-if="isImageConverting(image.image_id, image.image_url)"
                  class="report-thumb-loading"
                >
                  <i class="el-icon-loading"></i>
                </div>
              </div>
            </el-carousel-item>
          </el-carousel>
        </div>

        <div class="thumbnail-bar">
          <div
            v-for="(image, index) in images"
            :key="image.image_id"
            class="thumb-item"
            :class="{ active: activeThumbIndex === index }"
            @click="setActiveThumb(index)"
          >
            <img
              :src="getImageUrl(image.image_id, image.image_url)"
              class="thumb-image"
            />
            <div
              v-if="isImageConverting(image.image_id, image.image_url)"
              class="report-thumb-loading report-thumb-loading--compact"
            >
              <i class="el-icon-loading"></i>
            </div>

            <span class="thumb-delete-icon" @click.stop="handleDelete(index)">
              <i class="el-icon-close"></i>
            </span>

            <div
              v-if="image.recognition_status !== 'success'"
              class="thumb-error-overlay"
            >
              <i class="el-icon-error"></i>
            </div>
          </div>
        </div>

        <div class="action-buttons">
          <el-upload
            ref="imageUploader"
            style="display: none"
            action=""
            multiple
            :show-file-list="false"
            :limit="uploadLimit"
            :before-upload="beforeUpload"
            :on-exceed="handleExceed"
            :on-change="handleFileChange"
            :http-request="() => {}"
          >
          </el-upload>

          <div class="upload-controls-wrapper">
            <div
              class="custom-upload-button"
              :class="{ 'is-disabled': isUploadDisabled }"
              @click="triggerUpload"
            >
              <i class="el-icon-upload2"></i>
              <span>{{ $t("main.uploadArea.buttonText") }}</span>
            </div>
            <div class="upload-usage-hint">
              {{ $t("main.uploadArea.uploadVolume") }}{{ imageUploadCount }} /
              {{ maxUploadPerDay }}
            </div>
          </div>

          <div
            class="trigger-request-button"
            :class="{ 'is-loading': isAnalyzing }"
            @click="handleStartAnalysis"
          >
            <i :class="isAnalyzing ? 'el-icon-loading' : 'el-icon-top'"></i>
          </div>
        </div>
      </div>
      <footer class="page-footer is-pc-only">
        <a
          v-if="$t('footer.beian')"
          :href="'https://beian.miit.gov.cn/'"
          target="_blank"
          rel="noopener noreferrer"
          class="beian-link"
        >
          {{ $t("footer.beian") }}
        </a>
        <span v-if="$t('footer.beian') && $t('footer.copyright')"> | </span>
        {{ $t("footer.copyright") }}
      </footer>
    </div>

    <!-- 2. 右侧分析设置区 -->
    <div class="settings-sidebar">
      <div class="settings-header">
        <i class="el-icon-setting"></i>
        <h3>{{ $t("analytics.title") }}</h3>
      </div>

      <template v-if="currentRecognitionStatus === 'success'">
        <div class="settings-content">
          <div class="color-palette-section">
            <div v-if="currentRoiImageUrl" class="color-palette-image-wrapper">
              <img
                :src="currentRoiImageUrl"
                class="color-palette-image"
                alt="Image Region of Interest"
              />
              <div
                v-if="
                  isImageConverting(currentRoiImageId, currentRoiOriginalUrl)
                "
                class="report-thumb-loading report-thumb-loading--medium"
              >
                <i class="el-icon-loading"></i>
              </div>
            </div>
          </div>

          <div
            class="config-selector"
            v-if="
              currentImageCategory === 'Color Reproduction' ||
              currentImageCategory === 'Dynamic Range' ||
              currentImageCategory === 'Sharpness' ||
              currentImageCategory === 'Luminance Uniformity'
            "
          >
            <span class="config-label">{{ $t("analytics.configLabel") }}</span>
            <div class="config-controls">
              <el-select
                v-model="selectedProfileId"
                :placeholder="$t('analytics.selectPlaceholder')"
                class="config-select"
              >
                <el-option
                  v-for="profile in currentProfiles"
                  :key="profile.profile_id"
                  :label="$t('analytics.profileNames')[profile.profile_id - 1]"
                  :value="profile.profile_id"
                >
                </el-option>
              </el-select>
            </div>
          </div>

          <div class="table-container">
            <!-- Color Reproduction Table -->
            <div
              v-if="currentImageCategory === 'Color Reproduction'"
              style="height: 100%"
              key="color-reproduction-table"
            >
              <el-table
                :data="editableData"
                height="100%"
                class="settings-table"
                :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
                border
              >
                <el-table-column
                  prop="no"
                  label="No."
                  width="55"
                  min-width="45"
                  align="center"
                ></el-table-column>
                <el-table-column label="R (L)" align="center" min-width="70">
                  <template slot-scope="scope">
                    <el-input
                      v-if="
                        editingCell &&
                        editingCell.rowIndex === scope.$index &&
                        editingCell.prop === 'r_l'
                      "
                      v-model="scope.row.r_l"
                      size="mini"
                      ref="editInput"
                      @blur="handleInputBlur"
                      @input="validateNumericInput(scope.row, 'r_l')"
                    ></el-input>
                    <div
                      v-else
                      @click="handleCellClick(scope, 'r_l')"
                      class="cell-content"
                    >
                      <span>{{ scope.row.r_l }}</span>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column label="G (a)" align="center" min-width="70">
                  <template slot-scope="scope">
                    <el-input
                      v-if="
                        editingCell &&
                        editingCell.rowIndex === scope.$index &&
                        editingCell.prop === 'g_a'
                      "
                      v-model="scope.row.g_a"
                      size="mini"
                      ref="editInput"
                      @blur="handleInputBlur"
                      @input="validateNumericInput(scope.row, 'g_a')"
                    ></el-input>
                    <div
                      v-else
                      @click="handleCellClick(scope, 'g_a')"
                      class="cell-content"
                    >
                      <span>{{ scope.row.g_a }}</span>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column label="B (b)" align="center" min-width="70">
                  <template slot-scope="scope">
                    <el-input
                      v-if="
                        editingCell &&
                        editingCell.rowIndex === scope.$index &&
                        editingCell.prop === 'b_b'
                      "
                      v-model="scope.row.b_b"
                      size="mini"
                      ref="editInput"
                      @blur="handleInputBlur"
                      @input="validateNumericInput(scope.row, 'b_b')"
                    ></el-input>
                    <div
                      v-else
                      @click="handleCellClick(scope, 'b_b')"
                      class="cell-content"
                    >
                      <span>{{ scope.row.b_b }}</span>
                    </div>
                  </template>
                </el-table-column>
                <template slot="empty">
                  <el-empty :description="$t('main.noData')"></el-empty>
                </template>
              </el-table>
            </div>

            <!-- Dynamic Range Table -->
            <div
              v-else-if="currentImageCategory === 'Dynamic Range'"
              style="height: 100%"
              key="dynamic-range-table"
            >
              <el-table
                :data="editableData"
                height="100%"
                class="settings-table dynamic-range-table"
                :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
                border
              >
                <el-table-column
                  :label="$t('analytics.dynamic_config')"
                  align="center"
                >
                  <el-table-column prop="col1" align="center" min-width="60">
                    <template slot-scope="scope">
                      <el-input
                        v-if="
                          editingCell &&
                          editingCell.rowIndex === scope.$index &&
                          editingCell.prop === 'col1'
                        "
                        v-model="scope.row.col1"
                        size="mini"
                        ref="editInput"
                        @blur="handleInputBlur"
                        @input="validateNumericInput(scope.row, 'col1')"
                      ></el-input>
                      <div
                        v-else
                        @click="handleCellClick(scope, 'col1')"
                        class="cell-content"
                      >
                        <span>{{ scope.row.col1 }}</span>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column prop="col2" align="center" min-width="60">
                    <template slot-scope="scope">
                      <el-input
                        v-if="
                          editingCell &&
                          editingCell.rowIndex === scope.$index &&
                          editingCell.prop === 'col2'
                        "
                        v-model="scope.row.col2"
                        size="mini"
                        ref="editInput"
                        @blur="handleInputBlur"
                        @input="validateNumericInput(scope.row, 'col2')"
                      ></el-input>
                      <div
                        v-else
                        @click="handleCellClick(scope, 'col2')"
                        class="cell-content"
                      >
                        <span>{{ scope.row.col2 }}</span>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column prop="col3" align="center" min-width="60">
                    <template slot-scope="scope">
                      <el-input
                        v-if="
                          editingCell &&
                          editingCell.rowIndex === scope.$index &&
                          editingCell.prop === 'col3'
                        "
                        v-model="scope.row.col3"
                        size="mini"
                        ref="editInput"
                        @blur="handleInputBlur"
                        @input="validateNumericInput(scope.row, 'col3')"
                      ></el-input>
                      <div
                        v-else
                        @click="handleCellClick(scope, 'col3')"
                        class="cell-content"
                      >
                        <span>{{ scope.row.col3 }}</span>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column prop="col4" align="center" min-width="60">
                    <template slot-scope="scope">
                      <el-input
                        v-if="
                          editingCell &&
                          editingCell.rowIndex === scope.$index &&
                          editingCell.prop === 'col4'
                        "
                        v-model="scope.row.col4"
                        size="mini"
                        ref="editInput"
                        @blur="handleInputBlur"
                        @input="validateNumericInput(scope.row, 'col4')"
                      ></el-input>
                      <div
                        v-else
                        @click="handleCellClick(scope, 'col4')"
                        class="cell-content"
                      >
                        <span>{{ scope.row.col4 }}</span>
                      </div>
                    </template>
                  </el-table-column>
                </el-table-column>
                <template slot="empty">
                  <el-empty :description="$t('main.noData')"></el-empty>
                </template>
              </el-table>
            </div>

            <!-- Sharpness Table -->
            <div
              v-else-if="currentImageCategory === 'Sharpness'"
              style="height: 100%"
              key="sharpness-table"
            >
              <el-table
                :data="editableData"
                height="100%"
                class="settings-table"
                :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
                border
              >
                <el-table-column
                  :label="$t('analytics.sharpness_config')"
                  align="center"
                >
                  <template slot-scope="scope">
                    <el-input
                      v-if="
                        editingCell &&
                        editingCell.rowIndex === scope.$index &&
                        editingCell.prop === 'value'
                      "
                      v-model="scope.row.value"
                      size="mini"
                      ref="editInput"
                      @blur="handleInputBlur"
                      @input="validateNumericInput(scope.row, 'value')"
                    ></el-input>
                    <div
                      v-else
                      @click="handleCellClick(scope, 'value')"
                      class="cell-content"
                    >
                      <span>{{ scope.row.value }}</span>
                    </div>
                  </template>
                </el-table-column>
                <template slot="empty">
                  <el-empty :description="$t('main.noData')"></el-empty>
                </template>
              </el-table>
            </div>

            <!-- Luminance Uniformity Table -->
            <div
              v-else-if="currentImageCategory === 'Luminance Uniformity'"
              style="height: 100%"
              key="luminance-table"
            >
              <el-table
                :data="editableData"
                height="100%"
                class="settings-table"
                :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
                border
              >
                <el-table-column
                  :label="$t('analytics.horizontal_config')"
                  align="center"
                >
                  <template slot-scope="scope">
                    <el-input
                      v-if="
                        editingCell &&
                        editingCell.rowIndex === scope.$index &&
                        editingCell.prop === 'horizontal'
                      "
                      v-model="scope.row.horizontal"
                      size="mini"
                      ref="editInput"
                      @blur="handleInputBlur"
                      @input="validateNumericInput(scope.row, 'horizontal')"
                    ></el-input>
                    <div
                      v-else
                      @click="handleCellClick(scope, 'horizontal')"
                      class="cell-content"
                    >
                      <span>{{ scope.row.horizontal }}</span>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column
                  :label="$t('analytics.vertical_config')"
                  align="center"
                >
                  <template slot-scope="scope">
                    <el-input
                      v-if="
                        editingCell &&
                        editingCell.rowIndex === scope.$index &&
                        editingCell.prop === 'vertical'
                      "
                      v-model="scope.row.vertical"
                      size="mini"
                      ref="editInput"
                      @blur="handleInputBlur"
                      @input="validateNumericInput(scope.row, 'vertical')"
                    ></el-input>
                    <div
                      v-else
                      @click="handleCellClick(scope, 'vertical')"
                      class="cell-content"
                    >
                      <span>{{ scope.row.vertical }}</span>
                    </div>
                  </template>
                </el-table-column>
                <template slot="empty">
                  <el-empty :description="$t('main.noData')"></el-empty>
                </template>
              </el-table>
            </div>

            <div v-else style="height: 100%" key="no-data-table">
              <el-empty :description="$t('main.noData')"></el-empty>
            </div>
          </div>
        </div>

        <div
          class="settings-footer"
          v-if="
            currentImageCategory === 'Color Reproduction' ||
            currentImageCategory === 'Dynamic Range' ||
            currentImageCategory === 'Sharpness' ||
            currentImageCategory === 'Luminance Uniformity'
          "
        >
          <el-button
            type="primary"
            class="action-btn confirm-btn"
            :disabled="!isDataDirty"
            @click="handleConfirm"
            >{{ $t("analytics.confirmButton") }}</el-button
          >
          <el-button
            class="action-btn cancel-btn"
            :disabled="!isDataDirty"
            @click="handleCancel"
            >{{ $t("analytics.cancelButton") }}</el-button
          >
          <el-button class="action-btn reset-btn" @click="handleReset">{{
            $t("analytics.resetButton")
          }}</el-button>
        </div>
      </template>

      <div v-else class="settings-empty-content">
        <el-empty :description="$t('main.noData')"></el-empty>
      </div>
    </div>
    <footer class="page-footer is-mobile-only">
      <a
        v-if="$t('footer.beian')"
        :href="'https://beian.miit.gov.cn/'"
        target="_blank"
        rel="noopener noreferrer"
        class="beian-link"
      >
        {{ $t("footer.beian") }}
      </a>
      <span v-if="$t('footer.beian') && $t('footer.copyright')"> | </span>
      {{ $t("footer.copyright") }}
    </footer>
  </div>
</template>

<script>
import {
  deleteImage,
  updateAnalysisSettings,
  getDefaultAnalysisSettings,
  startAnalysis,
} from "@/api";
import UTIF from "utif";

export default {
  name: "AnalysisPanel",
  props: {
    images: {
      type: Array,
      required: true,
    },
    imageUploadCount: {
      type: Number,
      default: 0,
    },
    maxUploadPerDay: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      activeThumbIndex: 0,
      selectedProfileId: 1,
      editingCell: null,
      editableData: null,
      isDataDirty: false,
      isAnalyzing: false,
      convertedImageUrls: {}, // 存储转换后的 TIF 图片 URL，key 为 image_id
      convertingMap: {},
      conversionErrors: {},
    };
  },
  computed: {
    isUploadDisabled() {
      return this.images.length >= 3;
    },
    uploadLimit() {
      const limit = 3 - this.images.length;
      return limit > 0 ? limit : 0;
    },
    currentActiveImage() {
      return this.images[this.activeThumbIndex] || null;
    },
    currentProfiles() {
      const activeImage = this.images[this.activeThumbIndex];
      if (activeImage && activeImage.analysis_settings) {
        return activeImage.analysis_settings;
      }
      return [];
    },
    selectedProfileData() {
      if (!this.currentProfiles.length) {
        return null;
      }
      return (
        this.currentProfiles.find(
          (p) => p.profile_id === this.selectedProfileId
        ) || this.currentProfiles[0]
      );
    },
    currentImageCategory() {
      const activeImage = this.images[this.activeThumbIndex];
      return activeImage ? activeImage.category : null;
    },
    currentRecognitionStatus() {
      const activeImage = this.images[this.activeThumbIndex];
      return activeImage ? activeImage.recognition_status : null;
    },
    currentRoiImageId() {
      return this.currentActiveImage ? this.currentActiveImage.image_id : null;
    },
    currentRoiOriginalUrl() {
      return this.currentActiveImage ? this.currentActiveImage.image_url : null;
    },
    currentRoiImageUrl() {
      if (!this.currentRoiImageId || !this.currentRoiOriginalUrl) return null;
      return this.getImageUrl(
        this.currentRoiImageId,
        this.currentRoiOriginalUrl
      );
    },
    colorReproductionData() {
      if (
        this.currentImageCategory !== "Color Reproduction" ||
        !this.selectedProfileData ||
        !this.selectedProfileData.colorpath_char
      ) {
        return [];
      }
      return this.selectedProfileData.colorpath_char.map((row, index) => ({
        no: index + 1,
        r_l: row[0],
        g_a: row[1],
        b_b: row[2],
      }));
    },
    dynamicRangeData() {
      if (
        this.currentImageCategory !== "Dynamic Range" ||
        !this.selectedProfileData ||
        !this.selectedProfileData.denpath_char
      ) {
        return [];
      }
      const denpathChar = this.selectedProfileData.denpath_char;
      const result = [];
      for (let i = 0; i < denpathChar.length; i += 4) {
        if (i < denpathChar.length) {
          result.push({
            col1: denpathChar[i],
            col2: denpathChar[i + 1],
            col3: denpathChar[i + 2],
            col4: denpathChar[i + 3],
          });
        }
      }
      return result;
    },
    sharpnessData() {
      if (
        this.currentImageCategory !== "Sharpness" ||
        !this.selectedProfileData ||
        this.selectedProfileData.ISO_type === undefined
      ) {
        return [];
      }
      return [{ value: this.selectedProfileData.ISO_type }];
    },
    luminanceData() {
      if (
        this.currentImageCategory !== "Luminance Uniformity" ||
        !this.selectedProfileData ||
        this.selectedProfileData.horizontal_blocks === undefined ||
        this.selectedProfileData.vertical_blocks === undefined
      ) {
        return [];
      }
      return [
        {
          horizontal: this.selectedProfileData.horizontal_blocks,
          vertical: this.selectedProfileData.vertical_blocks,
        },
      ];
    },
  },
  methods: {
    /**
     * 检测 URL 是否为 TIF 格式
     */
    isTifUrl(url) {
      if (!url) return false;
      const urlLower = url.toLowerCase();
      return urlLower.endsWith(".tif") || urlLower.endsWith(".tiff");
    },
    /**
     * 判断图片是否正在转换
     */
    isImageConverting(imageId, originalUrl) {
      if (!imageId || !originalUrl) return false;
      return this.isTifUrl(originalUrl) && !!this.convertingMap[imageId];
    },
    /**
     * 获取图片 URL，如果是 TIF 格式则返回转换后的 URL
     */
    getImageUrl(imageId, originalUrl) {
      if (!originalUrl) return null;

      if (this.convertedImageUrls[imageId]) {
        return this.convertedImageUrls[imageId];
      }

      if (!this.isTifUrl(originalUrl)) {
        return originalUrl;
      }

      if (!this.convertingMap[imageId]) {
        this.convertTifImage(imageId, originalUrl);
      }

      return originalUrl;
    },
    /**
     * 转换 TIF 图片为可显示的格式
     */
    async convertTifImage(imageId, imageUrl) {
      if (this.convertingMap[imageId]) {
        return;
      }

      this.$set(this.convertingMap, imageId, true);
      this.$delete(this.conversionErrors, imageId);

      try {
        // 获取图片数据
        const response = await fetch(imageUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.statusText}`);
        }

        const arrayBuffer = await response.arrayBuffer();

        // 使用 UTIF 解码 TIF 文件
        const ifds = UTIF.decode(arrayBuffer);
        if (ifds.length === 0) {
          throw new Error("TIF 文件中没有找到图像数据");
        }

        // 解码第一个图像帧
        UTIF.decodeImage(arrayBuffer, ifds[0]);
        const width = ifds[0].width;
        const height = ifds[0].height;

        // 创建 canvas
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        // 将解码后的图像数据转换为 ImageData
        const imageData = ctx.createImageData(width, height);
        const rgba = UTIF.toRGBA8(ifds[0]);
        imageData.data.set(rgba);
        ctx.putImageData(imageData, 0, 0);

        // 将 canvas 转换为 PNG Data URL
        const dataUrl = canvas.toDataURL("image/png");

        // 存储转换后的 URL
        this.$set(this.convertedImageUrls, imageId, dataUrl);

        // 强制更新视图
        this.$forceUpdate();
      } catch (error) {
        console.error(`Failed to convert TIF image ${imageId}:`, error);
        this.$set(this.conversionErrors, imageId, error.message);
        // 转换失败时，使用原始 URL（虽然可能无法显示）
        this.$set(this.convertedImageUrls, imageId, imageUrl);
      } finally {
        this.$delete(this.convertingMap, imageId);
      }
    },
    async handleStartAnalysis() {
      if (this.isAnalyzing) return;

      const hasFailedImages = this.images.some(
        (img) => img.recognition_status !== "success"
      );
      if (hasFailedImages) {
        this.$notify.warning({
          title: this.$t("error.warningTitle"),
          message: this.$t("error.hasFailedImages"),
        });
        return;
      }

      const successfulImages = this.images.filter(
        (img) => img.recognition_status === "success"
      );
      if (successfulImages.length === 0) {
        this.$notify.warning({
          title: this.$t("error.warningTitle"),
          message: this.$t("error.noSuccessfulImages"),
        });
        return;
      }

      this.isAnalyzing = true;

      const analysisPromises = successfulImages.map((image) => {
        const analysis_type = image.category;
        const profileData = image.analysis_settings.find(
          (p) => p.profile_id === this.selectedProfileId
        );

        if (!profileData && analysis_type !== "Distortion") {
          return Promise.reject({
            imageId: image.image_id,
            reason: "Profile not found",
          });
        }

        let analysis_settings = {};
        switch (analysis_type) {
          case "Color Reproduction":
            analysis_settings.colorpath_char = profileData.colorpath_char;
            break;
          case "Dynamic Range":
            analysis_settings.denpath_char = profileData.denpath_char;
            break;
          case "Sharpness":
            analysis_settings.ISO_type = profileData.ISO_type;
            break;
          case "Luminance Uniformity":
            analysis_settings.horizontal_blocks = profileData.horizontal_blocks;
            analysis_settings.vertical_blocks = profileData.vertical_blocks;
            break;
          case "Distortion":
            analysis_settings = {};
            break;
          default:
            return Promise.reject({
              imageId: image.image_id,
              reason: "Unsupported analysis type",
            });
        }

        return startAnalysis(image.image_id, {
          param: { analysis_type, analysis_settings },
        });
      });

      const results = await Promise.allSettled(analysisPromises);
      this.isAnalyzing = false;

      const successfulSubmissions = results.filter(
        (r) => r.status === "fulfilled"
      ).length;
      const failedSubmissions = results.length - successfulSubmissions;

      if (successfulSubmissions > 0) {
        const successfulImageIds = [];
        results.forEach((result, index) => {
          if (result.status === "fulfilled") {
            successfulImageIds.push(successfulImages[index].image_id);
          }
        });

        if (failedSubmissions === 0) {
          this.$notify.success({
            title: this.$t("success.successTitle"),
            message: this.$t("success.analysisSubmittedAll", {
              count: successfulSubmissions,
            }),
          });
        } else {
          this.$notify.warning({
            title: this.$t("error.warningTitle"),
            message: this.$t("success.analysisSubmittedPartial", {
              successCount: successfulSubmissions,
              failCount: failedSubmissions,
            }),
          });
        }

        this.$emit("analysis-submitted", successfulImageIds);
      } else {
        this.$notify.error({
          title: this.$t("error.errorTitle"),
          message: this.$t("error.analysisSubmittedNone", {
            count: failedSubmissions,
          }),
        });
      }
    },

    validateNumericInput(row, prop) {
      this.$nextTick(() => {
        let value = row[prop];
        if (value === null || value === undefined) return;
        value = String(value);
        value = value.replace(/[^0-9.]/g, "");
        const dotIndex = value.indexOf(".");
        if (dotIndex !== -1) {
          const integerPart = value.substring(0, dotIndex + 1);
          const decimalPart = value.substring(dotIndex + 1).replace(/\./g, "");
          value = integerPart + decimalPart;
        }
        row[prop] = value;
      });
    },
    resetEditableData() {
      if (!this.selectedProfileData) {
        this.editableData = [];
        return;
      }
      switch (this.currentImageCategory) {
        case "Color Reproduction": {
          const sourceData = this.colorReproductionData;
          this.editableData = JSON.parse(JSON.stringify(sourceData));
          break;
        }
        case "Dynamic Range": {
          const sourceData = this.dynamicRangeData;
          this.editableData = JSON.parse(JSON.stringify(sourceData));
          break;
        }
        case "Sharpness": {
          const sourceData = this.sharpnessData;
          this.editableData = JSON.parse(JSON.stringify(sourceData));
          break;
        }
        case "Luminance Uniformity": {
          const sourceData = this.luminanceData;
          this.editableData = JSON.parse(JSON.stringify(sourceData));
          break;
        }
        default:
          this.editableData = [];
      }
      this.isDataDirty = false;
    },
    async handleConfirm() {
      const analysis_type = this.currentImageCategory;
      if (!analysis_type) {
        this.$notify.error({
          title: this.$t("error.errorTitle"),
          message: this.$t("error.noAnalysisType"),
        });
        return;
      }
      let payload;
      switch (analysis_type) {
        case "Color Reproduction": {
          const profile_data = {
            colorpath_char: this.editableData.map((row) => [
              parseFloat(row.r_l),
              parseFloat(row.g_a),
              parseFloat(row.b_b),
            ]),
          };
          payload = {
            analysis_type,
            profile_id: this.selectedProfileId,
            profile_data,
          };
          break;
        }
        case "Dynamic Range": {
          const profile_data = {
            denpath_char: this.editableData.flatMap((row) =>
              [row.col1, row.col2, row.col3, row.col4]
                .filter((v) => v !== undefined)
                .map(parseFloat)
            ),
          };
          payload = {
            analysis_type,
            profile_id: this.selectedProfileId,
            profile_data,
          };
          break;
        }
        case "Sharpness": {
          const profile_data = {};
          if (this.editableData.length > 0) {
            profile_data.ISO_type = parseInt(this.editableData[0].value, 10);
          }
          payload = {
            analysis_type,
            profile_id: this.selectedProfileId,
            profile_data,
          };
          break;
        }
        case "Luminance Uniformity": {
          const profile_data = {};
          if (this.editableData.length > 0) {
            profile_data.horizontal_blocks = parseInt(
              this.editableData[0].horizontal,
              10
            );
            profile_data.vertical_blocks = parseInt(
              this.editableData[0].vertical,
              10
            );
          }
          payload = {
            analysis_type,
            profile_id: this.selectedProfileId,
            profile_data,
          };
          break;
        }
        case "Distortion": {
          payload = { analysis_type };
          break;
        }
        default:
          this.$notify.warning({
            title: this.$t("error.warningTitle"),
            message: this.$t("error.notImplemented", { type: analysis_type }),
          });
          return;
      }
      try {
        await updateAnalysisSettings(payload);
        if (analysis_type !== "Distortion") {
          Object.assign(this.selectedProfileData, payload.profile_data);
        }
        this.isDataDirty = false;
        this.$notify.success({
          title: this.$t("success.successTitle"),
          message: this.$t("success.updateSuccess", { type: analysis_type }),
        });
      } catch (error) {
        console.log("请求失败，但错误已被捕获，页面不会崩溃。", error);
      }
    },
    handleCancel() {
      this.resetEditableData();
      this.$notify.success({
        title: this.$t("success.successTitle"),
        message: this.$t("success.changesReverted"),
      });
    },
    handleReset() {
      const analysis_type = this.currentImageCategory;
      if (!analysis_type) {
        this.$notify.error({
          title: this.$t("error.errorTitle"),
          message: this.$t("error.noAnalysisType"),
        });
        return;
      }
      this.$confirm(
        this.$t("confirm.resetToDefaults"),
        this.$t("confirm.attentionTitle"),
        {
          confirmButtonText: this.$t("buttons.yes"),
          cancelButtonText: this.$t("buttons.no"),
          type: "warning",
          customClass: "responsive-confirm-box",
        }
      )
        .then(async () => {
          try {
            const response = await getDefaultAnalysisSettings(analysis_type);
            const defaultConfig = response.data.default_config;
            switch (analysis_type) {
              case "Color Reproduction": {
                const newEditableData = defaultConfig.colorpath_char.map(
                  (row, index) => ({
                    no: index + 1,
                    r_l: row[0],
                    g_a: row[1],
                    b_b: row[2],
                  })
                );
                this.editableData = newEditableData;
                break;
              }
              case "Dynamic Range": {
                const denpathChar = defaultConfig.denpath_char;
                const newEditableData = [];
                for (let i = 0; i < denpathChar.length; i += 4) {
                  newEditableData.push({
                    col1: denpathChar[i],
                    col2: denpathChar[i + 1],
                    col3: denpathChar[i + 2],
                    col4: denpathChar[i + 3],
                  });
                }
                this.editableData = newEditableData;
                break;
              }
              case "Sharpness": {
                const newEditableData = [{ value: defaultConfig.ISO_type }];
                this.editableData = newEditableData;
                break;
              }
              case "Luminance Uniformity": {
                const newEditableData = [
                  {
                    horizontal: defaultConfig.horizontal_blocks,
                    vertical: defaultConfig.vertical_blocks,
                  },
                ];
                this.editableData = newEditableData;
                break;
              }
            }
            this.$notify.success({
              title: this.$t("success.successTitle"),
              message: this.$t("success.defaultsLoaded"),
            });
          } catch (error) {
            console.log("请求失败，但错误已被捕获，页面不会崩溃。", error);
          }
        })
        .catch(() => {
          console.log("Reset canceled by user");
        });
    },
    triggerUpload() {
      if (this.isUploadDisabled) {
        return;
      }
      this.$refs.imageUploader.$el.querySelector("input").click();
    },
    handleExceed() {
      this.$notify.warning({
        title: this.$t("error.warningTitle"),
        message: this.$t("error.uploadLimitThree"),
      });
    },
    beforeUpload(file) {
      const supportedTypes = [
        "image/jpeg",
        "image/png",
        "image/bmp",
        "image/tiff",
      ];
      const isTypeValid = supportedTypes.includes(file.type);
      // const isSizeValid = file.size / 1024 / 1024 < 50;
      if (!isTypeValid) {
        this.$notify.warning({
          title: this.$t("error.warningTitle"),
          message: this.$t("error.uploadTypeInvalid"),
        });
        return false;
      }
      // if (!isSizeValid) {
      //   this.$notify.warning({
      //     title: this.$t("error.warningTitle"),
      //     message: this.$t("error.uploadSizeExceed"),
      //   });
      //   return false;
      // }
      return isTypeValid;
    },
    handleFileChange(file, fileList) {
      this.$emit("add-images", fileList);
      this.$nextTick(() => {
        this.$refs.imageUploader.clearFiles();
      });
    },
    handleCellClick(scope, prop) {
      this.editingCell = {
        rowIndex: scope.$index,
        prop: prop,
      };
      this.$nextTick(() => {
        if (this.$refs.editInput) {
          this.$refs.editInput.focus();
        }
      });
    },
    handleInputBlur() {
      this.editingCell = null;
    },
    handleCarouselChange(newIndex) {
      this.activeThumbIndex = newIndex;
      this.updateSidebarCategory(newIndex);
      this.selectedProfileId = 1;
    },
    setActiveThumb(index) {
      this.$refs.mainCarousel.setActiveItem(index);
    },
    updateSidebarCategory(index) {
      const activeImage = this.images[index];
      if (activeImage) {
        this.$emit("category-change", activeImage.category);
      }
    },
    handleDelete(index) {
      const imageToDelete = this.images[index];
      if (!imageToDelete) return;
      const imageId = imageToDelete.image_id;
      this.$confirm(
        this.$t("confirm.deleteImage"),
        this.$t("confirm.attentionTitle"),
        {
          confirmButtonText: this.$t("buttons.yes"),
          cancelButtonText: this.$t("buttons.no"),
          type: "warning",
          customClass: "responsive-confirm-box",
        }
      )
        .then(async () => {
          try {
            await deleteImage(imageId);
            // 清理转换缓存
            this.$delete(this.convertedImageUrls, imageId);
            this.$delete(this.convertingMap, imageId);
            this.$delete(this.conversionErrors, imageId);
            this.$notify.success({
              title: this.$t("success.successTitle"),
              message: this.$t("success.imageDelSuccess"),
            });
            this.$emit("image-deleted", imageId);
          } catch (error) {
            console.log("请求失败，但错误已被捕获，页面不会崩溃。", error);
          }
        })
        .catch(() => {
          console.log("Delete canceled by user");
        });
    },
  },
  watch: {
    editableData: {
      handler(newValue) {
        if (!newValue) {
          this.isDataDirty = false;
          return;
        }
        let originalData;
        switch (this.currentImageCategory) {
          case "Color Reproduction":
            originalData = this.colorReproductionData;
            break;
          case "Dynamic Range":
            originalData = this.dynamicRangeData;
            break;
          case "Sharpness":
            originalData = this.sharpnessData;
            break;
          case "Luminance Uniformity":
            originalData = this.luminanceData;
            break;
          default:
            originalData = [];
        }
        if (JSON.stringify(newValue) !== JSON.stringify(originalData)) {
          this.isDataDirty = true;
        } else {
          this.isDataDirty = false;
        }
      },
      deep: true,
    },
    images: {
      immediate: true,
      handler(newImages, oldImages) {
        if (oldImages && oldImages.length > 0) {
          const oldIds = new Set(oldImages.map((img) => img.image_id));
          const newIds = new Set((newImages || []).map((img) => img.image_id));

          oldIds.forEach((id) => {
            if (!newIds.has(id)) {
              this.$delete(this.convertedImageUrls, id);
              this.$delete(this.convertingMap, id);
              this.$delete(this.conversionErrors, id);
            }
          });
        }

        if (newImages && newImages.length > 0) {
          this.activeThumbIndex = 0;
          this.selectedProfileId = 1;
          this.$nextTick(() => {
            if (this.$refs.mainCarousel) {
              this.$refs.mainCarousel.setActiveItem(0);
            }
          });
          this.updateSidebarCategory(0);

          newImages.forEach((image) => {
            if (image.image_url && this.isTifUrl(image.image_url)) {
              this.getImageUrl(image.image_id, image.image_url);
            }
          });
        }
      },
    },
    selectedProfileData: {
      handler() {
        this.resetEditableData();
      },
      immediate: true,
      deep: true,
    },
    activeThumbIndex(newIndex) {
      this.selectedProfileId = 1;
      this.updateSidebarCategory(newIndex);
    },
  },
};
</script>

<style scoped>
.dynamic-range-table ::v-deep .el-table__header tr:nth-child(2) {
  display: none;
}
.cell-content {
  cursor: pointer;
  padding: 5px;
}
.analysis-panel-wrapper {
  display: flex;
  gap: 20px;
  width: 100%;
  max-width: 100%;
  height: 100%;
  padding-right: 2.5%;
  padding-left: 3.5%;
  box-sizing: border-box;
}
.center-content-area {
  flex: 1 1 0%;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding-right: 2.5%;
  overflow: hidden;
}
.main-preview-area {
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.03);
  min-height: 0;
  margin-top: 4%;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: visible;
}
.page-footer {
  text-align: center;
  color: #909399;
  font-size: 12px;
  padding: 20px 0;
  flex-shrink: 0;
  margin-top: auto;
}
.beian-link {
  color: #909399;
  text-decoration: none;
  transition: color 0.3s;
}
.beian-link:hover {
  color: #00a0e9;
  text-decoration: underline;
}
.is-mobile-only {
  display: none;
}
.carousel-wrapper {
  position: relative;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  overflow: hidden;
  flex-shrink: 0;
}
.el-carousel {
  width: 100%;
  height: 38vh;
  min-width: 0;
  flex-shrink: 0;
}
::v-deep .el-carousel__arrow--left {
  left: 12%;
}
::v-deep .el-carousel__arrow--right {
  right: 12%;
}
.carousel-image-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  border-radius: 6px;
  width: 100%;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
}
.carousel-image {
  max-width: 100%;
  max-height: 70%;
  object-fit: contain;
  flex-shrink: 0;
}
.report-thumb-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.78);
  z-index: 5;
  pointer-events: none;
}
.report-thumb-loading .el-icon-loading {
  font-size: 32px;
  color: #409eff;
}
.report-thumb-loading--medium .el-icon-loading {
  font-size: 24px;
}
.report-thumb-loading--compact {
  background-color: rgba(255, 255, 255, 0.6);
}
.report-thumb-loading--compact .el-icon-loading {
  font-size: 18px;
}
.thumbnail-bar {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 35px;
  flex-wrap: wrap;
  flex-shrink: 0;
  border-bottom: 1px solid #ebeef5;
  padding-bottom: 40px;
  min-width: 0;
  overflow: hidden;
}
.thumb-item {
  position: relative;
  width: 100px;
  height: 75px;
  cursor: pointer;
  border-radius: 6px;
  overflow: hidden;
  transition: all 0.3s;
  border: 2px solid transparent;
}
.thumb-item.active {
  border-color: #00a0e9;
  box-shadow: 0 0 8px rgba(0, 160, 233, 0.4);
}
.thumb-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  max-width: 100%; /* 防止错误图标拉伸 */
  max-height: 100%; /* 防止错误图标拉伸 */
  display: block; /* 移除图片底部空白 */
}
.thumb-error-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(39, 38, 38, 0.7);
  color: rgba(245, 108, 108, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  pointer-events: none;
  z-index: 1;
}
.thumb-delete-icon {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 18px;
  height: 18px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: 2;
}
.thumb-item:hover .thumb-delete-icon {
  opacity: 1;
}
.action-buttons {
  position: relative;
  margin-top: 0;
  padding-top: 40px;
  padding-bottom: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  z-index: 10;
  min-height: 100px;
}
.upload-controls-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.upload-usage-hint {
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
}
.custom-upload-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  padding: 0 20px;
  background-color: #00a0e9;
  color: #fff;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s;
  border: 1px solid transparent;
}
.custom-upload-button:hover {
  background-color: #66b1ff;
}
.custom-upload-button.is-disabled {
  background-color: #a0cfff;
  border-color: #a0cfff;
  cursor: not-allowed;
  opacity: 0.6;
}
.custom-upload-button i {
  font-size: 20px;
  margin-right: 8px;
}
.custom-upload-button span {
  font-size: 14px;
}
.trigger-request-button {
  position: absolute;
  right: 12%;
  top: 59px;
  width: 38px;
  height: 38px;
  background-color: #00a0e9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #fff;
  cursor: pointer;
  transition: background-color 0.3s;
  z-index: 11;
}
.trigger-request-button i {
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.trigger-request-button:hover {
  background-color: #66b1ff;
}
.settings-sidebar {
  width: 377px;
  flex: 0 0 377px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
.settings-header {
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  flex-shrink: 0;
}
.settings-header i {
  font-size: 20px;
}
.settings-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0 20px;
  overflow: hidden;
}
.settings-empty-content {
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.color-palette-section {
  margin-bottom: 15px;
  text-align: center;
}
.color-palette-image {
  max-width: 166px;
  width: 100%;
  border-radius: 4px;
  border: 1px solid #ebeef5;
}
.color-palette-image-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.color-palette-image-wrapper .report-thumb-loading {
  border-radius: 4px;
}
.config-selector {
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.config-label {
  font-weight: 500;
  color: #606266;
  flex-shrink: 0;
}
.config-controls {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
}
.config-select {
  flex: 1;
}
.new-config-btn {
  flex-shrink: 0;
}
.table-container {
  flex-grow: 1;
  min-height: 0;
  overflow: auto;
  flex-shrink: 1;
}
.settings-table {
  width: 100%;
  height: 100%;
}
.settings-table ::v-deep .el-table__cell {
  padding: 8px 4px;
}
.settings-table ::v-deep .el-table__header th {
  padding: 8px 4px;
}
.settings-table .el-table__header {
  background-color: #f5f7fa;
}
.settings-table .el-table__body tr:hover {
  background-color: #f5f7fa;
}
.settings-footer {
  margin-top: auto;
  flex-shrink: 0;
  padding: 20px;
  display: flex;
  justify-content: center;
  gap: 10px;
  min-height: 60px;
  box-sizing: border-box;
}
.action-btn {
  margin: 0;
  flex-shrink: 0;
  min-width: 60px;
  white-space: nowrap;
}
.confirm-btn {
  background-color: #00a0e9;
  border-color: #00a0e9;
}
.confirm-btn.is-disabled,
.confirm-btn.is-disabled:hover {
  background-color: #a0cfff;
  border-color: #a0cfff;
}
.cancel-btn {
  background-color: #fff;
  border-color: #dcdfe6;
  color: #606266;
}
.cancel-btn.is-disabled,
.cancel-btn.is-disabled:hover {
  background-color: #fff;
  border-color: #e4e7ed;
  color: #c0c4cc;
}
.reset-btn {
  color: #606266;
  border: 1px solid #dcdfe6;
}
@media (max-width: 768px) {
  .analysis-panel-wrapper {
    flex-direction: column;
    padding-right: 15px;
    padding-left: 15px;
    gap: 15px;
    min-width: auto;
    overflow-x: visible;
  }
  .center-content-area {
    min-width: auto;
    flex-shrink: 1;
    padding-right: 0;
  }
  .main-preview-area {
    margin-top: 15px;
    margin-right: 0;
    padding: 15px;
    flex-shrink: 1;
  }
  .settings-sidebar {
    width: auto;
    min-width: auto;
    height: auto;
    margin-top: 15px;
    overflow: visible;
  }
  .settings-content {
    overflow: visible;
  }
  .table-container {
    overflow: visible;
  }
  .settings-footer {
    min-height: auto;
  }
  .el-carousel {
    height: 30vh;
  }
  .carousel-wrapper,
  .carousel-image-container,
  .carousel-image {
    flex-shrink: 1;
  }
  ::v-deep .el-carousel__arrow--left {
    left: 5%;
  }
  ::v-deep .el-carousel__arrow--right {
    right: 5%;
  }
  .thumbnail-bar {
    margin-top: 20px;
    padding-bottom: 20px;
    gap: 10px;
  }
  .thumb-item {
    width: 80px;
    height: 60px;
  }
  .action-buttons {
    padding-top: 20px;
    gap: 15px;
    flex-direction: row;
    justify-content: center;
    align-items: flex-start;
  }
  .upload-controls-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
  }
  .trigger-request-button {
    position: static;
    right: auto;
    top: auto;
    margin-left: 15px;
    margin-top: 0;
    flex-shrink: 0;
    align-self: flex-start;
  }
  .page-footer {
    padding: 15px 0;
  }
  .is-pc-only {
    display: none;
  }
  .is-mobile-only {
    display: block;
    margin-top: 0;
    padding: 15px 0;
  }
  .settings-footer {
    flex-wrap: wrap;
  }
  .settings-table ::v-deep .is-pc-only-column {
    display: none;
  }
  .settings-content {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 0 6px;
  }
}
</style>
<style>
@media (max-width: 768px) {
  .responsive-confirm-box {
    width: 90%;
    max-width: 400px;
  }
}
</style>
